import { PropsWithChildren } from 'react';
import { Button, Spinner } from '@ds';
import { checkStatusAndRouteAllowed } from '@lp/features/CaseLogistics/utils';
import { Case, CaseStatus, RoutePlanStatus, ServiceType } from '@/api/apis/case';
import { routeStatusNames } from '@/constants/case/case.constants';
import { useCaseByIdQuery, useCaseResourcesDataQuery } from '@/queries/case';
import { useLogisticsById } from '@/queries/route-planner';
import { RouteFilterType } from '@/types/route';
import { CaseDetailsLogistics } from './components/CaseDetailsLogistics';
import { EmptyLogistics } from './components/EmptyLogistics';
import { hasDryRunRouteLegs } from '../RoutePlanner/utils';

type CaseLogisticsProps = {
  caseId: string;
};

type LogisticsLayoutProps = {
  status?: CaseStatus;
  routePlanningStatus?: RoutePlanStatus;
};

const LogisticsLayout = ({ status, routePlanningStatus, children }: PropsWithChildren<LogisticsLayoutProps>) => (
  <div className="flex flex-col h-full min-w-105.5 grow gap-6 p-6 items-start">
    <div className="flex flex-row justify-between items-center w-full">
      <h3>Case Logistics</h3>
      <Button variant="solid" size="sm" isDisabled>
        {status && routePlanningStatus && checkStatusAndRouteAllowed(status, routePlanningStatus)
          ? routeStatusNames[routePlanningStatus]
          : 'No route status'}
      </Button>
    </div>
    {children}
  </div>
);

const customFilterTitles = {
  [RouteFilterType.Full]: 'Full route',
  [RouteFilterType.CS]: 'Clinical Specialist’s route',
  [RouteFilterType.Surgeon]: 'Retrieval Surgeon’s route',
};

export const CaseLogistics = ({ caseId }: CaseLogisticsProps) => {
  const { data } = useCaseByIdQuery(caseId);
  const { data: caseResourcesData } = useCaseResourcesDataQuery(caseId);
  const { data: logisticsProjection, isFetched } = useLogisticsById(data?.logisticsReference?.id);
  const caseData = data && caseResourcesData ? ({ ...data, ...caseResourcesData } as Case) : undefined;

  const isDataFetching = data?.logisticsReference?.id && !isFetched;

  if (!caseData || isDataFetching)
    return (
      <LogisticsLayout>
        <Spinner />
      </LogisticsLayout>
    );
  const { donorHospitalDetails, serviceType } = caseData;

  const showDryRunRoute = hasDryRunRouteLegs(logisticsProjection);
  const isDA = serviceType === ServiceType.DirectAcquisition;
  const hasLogistics = !!data?.routePlanningStatus && !!logisticsProjection;
  const showEmptyLogistics = isDA || !hasLogistics;
  const showLogisticsDetails = !!donorHospitalDetails && hasLogistics && !isDA;

  return (
    <LogisticsLayout status={caseData.status} routePlanningStatus={caseData.routePlanningStatus}>
      {showLogisticsDetails && (
        <CaseDetailsLogistics
          key={`${caseId}-logistics`}
          caseId={caseId}
          donorHospital={donorHospitalDetails}
          customFilterTitles={customFilterTitles}
          showDryRunRoute={showDryRunRoute}
        />
      )}
      {showEmptyLogistics && <EmptyLogistics key={caseId} donorHospital={donorHospitalDetails} />}
    </LogisticsLayout>
  );
};
